"use client";

import { useEffect, useId, useRef, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import { saveLead } from "@/lib/leads";
import { whatsappLink } from "@/site.config";

const BUSINESS = [
  "Real estate",
  "Clinic or hospital",
  "Salon or spa",
  "Restaurant or cafe",
  "Retail business",
  "Gym or fitness",
  "Coaching or school",
  "Travel agency",
  "Car or bike dealer",
  "Builder or contractor",
  "Manufacturing",
  "Services business",
  "Other",
];

type LeadSource = Parameters<typeof saveLead>[0]["source"];

export default function LeadCaptureModal() {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("modal");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [website, setWebsite] = useState("");
  const [successUrl, setSuccessUrl] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const startedRef = useRef(false);

  const subline = "Tell us where to reach you and we will start the call on WhatsApp.";

  useEffect(() => {
    function onDocumentClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest<HTMLElement>("[data-lead-cta]");
      if (!trigger) return;

      event.preventDefault();
      triggerRef.current = trigger;
      setSource(trigger.dataset.leadSource || "modal");
      setSubmitted(false);
      setSuccessUrl("");
      setOpen(true);

      if (!startedRef.current) {
        startedRef.current = true;
        trackEvent("form_started", { source: trigger.dataset.leadSource || "modal" });
      }
    }

    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function getFocusable() {
      return Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || []).filter(
        (element) => !element.hasAttribute("disabled") && !element.getAttribute("aria-hidden"),
      );
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    const focusTimer = window.setTimeout(() => {
      const firstField = dialogRef.current?.querySelector<HTMLElement>("#lcm-name");
      firstField?.focus();
    }, 30);

    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  function closeModal() {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }

  function onBackdropClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  }

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (website.trim()) {
      const url = whatsappLink({ name: trimmedName, business });
      setSuccessUrl(url);
      setSubmitted(true);
      return;
    }

    if (!trimmedName || !trimmedPhone) return;

    saveLead({
      name: trimmedName,
      phone: trimmedPhone,
      business,
      source: source as LeadSource,
      consent: true,
      website,
    });
    trackEvent("form_submitted", { source, has_business: Boolean(business) });

    const url = whatsappLink({ name: trimmedName, business });
    setSuccessUrl(url);
    setSubmitted(true);
    trackEvent("whatsapp_opened", { source, business });
    window.location.href = url;
  }

  if (!open) return null;

  return (
    <div className="lcm-overlay" onMouseDown={onBackdropClick}>
      <div
        ref={dialogRef}
        className="lcm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <button type="button" className="lcm-close" onClick={closeModal} aria-label="Close">
          ×
        </button>

        {submitted ? (
          <div className="lcm-success">
            <h2 id={titleId} className="lcm-title">
              Opening WhatsApp...
            </h2>
            <p className="lcm-subline">Your free call request is ready in WhatsApp.</p>
            <a className="btn btn-primary lcm-submit" href={successUrl}>
              Continue on WhatsApp
            </a>
          </div>
        ) : (
          <form className="lcm-form" onSubmit={onSubmit}>
            <div className="lcm-head">
              <h2 id={titleId} className="lcm-title">
                Get your free call
              </h2>
              <p className="lcm-subline">{subline}</p>
            </div>

            <label className="lcm-field" htmlFor="lcm-name">
              <span>Your name</span>
              <input
                id="lcm-name"
                type="text"
                name="name"
                autoComplete="name"
                placeholder="Your name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>

            <label className="lcm-field" htmlFor="lcm-phone">
              <span>WhatsApp number</span>
              <input
                id="lcm-phone"
                type="tel"
                name="phone"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Your WhatsApp number"
                required
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </label>

            <label className="lcm-field" htmlFor="lcm-business">
              <span>Business type</span>
              <select
                id="lcm-business"
                name="business"
                value={business}
                onChange={(event) => setBusiness(event.target.value)}
                required
              >
                <option value="" disabled>Choose your business type</option>
                {BUSINESS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <input
              type="text"
              name="website"
              className="lcm-hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />

            <p className="lcm-reassurance">We will WhatsApp you. No spam.</p>

            <button type="submit" className="btn btn-primary lcm-submit">
              Start on WhatsApp
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
